import Image from 'next/image';
import { SectionKicker } from '@/components/brand/section-kicker';
import { Reveal } from '@/components/motion/reveal';
import { TypographyH1, TypographyLead } from '@/components/ui/typography';

/**
 * Shared hero for every sub-page. Enforces a consistent minimum height and
 * vertically centers the title block, so pages with only a heading (e.g. the
 * legal pages) look identical to pages with a long subheading — the skyline and
 * spacing never collapse. The skyline is nudged down a touch and its lower edge
 * is dissolved into the page. No breadcrumb by design.
 */
export function PageHero({
  overline,
  heading,
  subheading,
  children,
}: {
  overline: string;
  heading: string;
  subheading?: string;
  children?: React.ReactNode;
}) {
  return (
    <header className="relative isolate flex min-h-[20rem] items-center overflow-hidden sm:min-h-[24rem]">
      {/* Skyline backdrop — offset down so there's open space above it. */}
      <div className="pointer-events-none absolute inset-0 -z-10 select-none" aria-hidden>
        <Image
          src="/features-bg.webp"
          alt=""
          fill
          sizes="100vw"
          priority
          // Full-bleed background: below lg the header is narrow enough that
          // `contain` already fills the width (and keeps the side buildings);
          // at lg+ the header turns wide, so `cover` fills it edge-to-edge,
          // anchored to the top so the skyline (not the dunes) stays in frame.
          className="object-contain object-bottom lg:object-cover lg:object-top"
        />
      </div>
      {/* Dissolve the bottom of the hero into the page — a slim band, anchored
          to the header so it always completes in view. Kept shallow so the
          skyline reads clearly rather than washing out. */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-5"
        style={{ background: 'linear-gradient(to bottom, transparent, var(--background))' }}
        aria-hidden
      />

      <div className="container-brand relative w-full">
        <Reveal className="mx-auto max-w-3xl py-12 text-center sm:py-16">
          <SectionKicker>{overline}</SectionKicker>
          <TypographyH1 className="mt-5 text-balance font-display font-medium text-primary">
            {heading}
          </TypographyH1>
          {subheading ? (
            <TypographyLead className="mx-auto mt-4 max-w-xl">{subheading}</TypographyLead>
          ) : null}
          {children ? <div className="mt-8 sm:mt-10">{children}</div> : null}
        </Reveal>
      </div>
    </header>
  );
}
